import * as React from 'react'
import { ReactElement, ReactNode } from 'react'
import clx from 'classnames'
import { DropdownIcon } from '@/app/features/icons/Dropdown'
import { NavLink, useLocation } from 'react-router-dom'
import { FaIcon } from '@/lib/fa'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { SidebarTooltip } from './SidebarTooltip'

type Props = {
  path?: string
  href?: string
  hasParent?: boolean
  name?: string
  disabled?: boolean
  label: string
  collapsed?: boolean
  title?: string
  displayArrow?: boolean
  children?: ReactNode
  hasChildrenExpanded?: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
  activeMatchPattern?: string
  solidIcon?: IconProp
  regularIcon?: IconProp
  logout?: boolean
}

/* ---------- Helpers ---------- */

// Renders either SVG icon or FontAwesome icon with consistent size
const MenuIcon = ({
  solidIcon,
  regularIcon,
  isActive,
  logout = false,
}: {
  solidIcon?: IconProp
  regularIcon?: IconProp
  isActive: boolean
  logout?: boolean
}) => {
  if (isActive) {
    return (
      <FaIcon
        icon={solidIcon!}
        className={clx(
          'tw-h-4 tw-w-4',
          logout && 'tw-text-error -tw-scale-x-100',
        )}
      />
    )
  } else {
    return (
      <FaIcon
        icon={regularIcon!}
        className={clx(
          'tw-h-4 tw-w-4',
          logout && 'tw-text-error -tw-scale-x-100',
        )}
      />
    )
  }
}

// Menu label (hidden when sidebar is collapsed)
const MenuLabel = ({
  label,
  logout = false,
}: {
  label: string
  logout?: boolean
}) => (
  <span
    className={clx(
      'tw-hidden tw-text-sm tw-capitalize md:tw-block',
      logout && 'tw-text-error',
    )}
  >
    {label}
  </span>
)

/* ---------- Classes ---------- */

const baseItemClass =
  'tw-flex tw-items-center tw-gap-2 tw-text-content-base hover:tw-bg-base-200 hover:tw-text-content-base tw-rounded hover:tw-no-underline'

const parentItemClass = clx(baseItemClass, 'tw-p-1.5 tw-text-xs')
const itemClass = clx(
  baseItemClass,
  'tw-p-2 sm:tw-gap-2 tw-w-full tw-items-center',
)

const activeClass =
  'tw-flex-grow tw-bg-content-neutral tw-text-focus-100 tw-rounded-[4px]'

/* ---------- Component ---------- */

export const SidebarMenuBtn = ({
  path,
  href,
  disabled,
  label,
  title,
  hasParent,
  collapsed,
  displayArrow,
  name,
  children,
  hasChildrenExpanded,
  onClick,
  activeMatchPattern,
  solidIcon,
  regularIcon,
  logout = false,
}: Props) => {
  const hasSubItems = React.Children.count(children) > 0
  const location = useLocation()

  // Check if current route is active
  const currentPath = href || path
  const isActive = React.useMemo(() => {
    if (activeMatchPattern) {
      return location.pathname.includes(activeMatchPattern)
    }
    if (currentPath) {
      return (
        location.pathname === currentPath ||
        location.pathname.startsWith(currentPath + '/')
      )
    }
    return false
  }, [location.pathname, currentPath, activeMatchPattern])

  const wrapperClass = clx(
    itemClass,
    'tw-justify-center',
    !collapsed && 'md:tw-justify-start',
    logout && 'hover:tw-bg-error/10',
  )

  const iconEl = (
    <MenuIcon
      isActive={isActive}
      regularIcon={regularIcon}
      solidIcon={solidIcon}
      logout={logout}
    />
  )

  // Hide label when sidebar is collapsed
  const labelEl = collapsed ? null : <MenuLabel label={label} logout={logout} />

  const withTooltip = (
    node: ReactElement,
    wrapperClassName?: string,
  ): ReactElement =>
    collapsed ? (
      <SidebarTooltip text={label} className={wrapperClassName}>
        {node}
      </SidebarTooltip>
    ) : (
      node
    )

  /* ---------- Parent item ---------- */
  if (hasParent) {
    return withTooltip(
      path ? (
        <NavLink
          to={path}
          className={clx(parentItemClass, isActive && activeClass)}
        >
          {iconEl}
          {labelEl}
        </NavLink>
      ) : (
        <span className={parentItemClass}>
          {iconEl}
          {labelEl}
        </span>
      ),
    )
  }

  /* ---------- Item with children ---------- */
  if (hasSubItems) {
    // If has both path and children, make it a NavLink that also handles onClick
    const content = (
      <>
        <span className="tw-flex tw-items-center tw-gap-2">
          {iconEl}
          {labelEl}
        </span>

        {/* Hide dropdown arrow when collapsed */}
        {displayArrow && !collapsed && (
          <span className="tw-hidden md:tw-inline-block">
            <DropdownIcon
              className={clx('tw-transition-transform', {
                'tw-rotate-180': hasChildrenExpanded,
              })}
            />
          </span>
        )}
      </>
    )

    const trigger = path ? (
      <NavLink
        to={path}
        onClick={onClick}
        title={title}
        className={clx(
          itemClass,
          collapsed ? 'tw-justify-center' : 'tw-justify-between md:tw-w-full',
          isActive && activeClass,
        )}
      >
        {logout ? ' test' : content}
      </NavLink>
    ) : (
      <button
        type="button"
        onClick={onClick}
        name={name}
        disabled={disabled}
        title={title}
        className={clx(
          itemClass,
          collapsed ? 'tw-justify-center' : 'tw-justify-between md:tw-w-full',
        )}
      >
        {content}
      </button>
    )

    return (
      <>
        {withTooltip(trigger, 'tw-w-full')}

        {(hasChildrenExpanded || (collapsed && isActive)) && (
          <ul
            className={clx(
              'tw-flex tw-flex-col',
              collapsed
                ? 'tw-items-center tw-gap-1 tw-px-1 tw-py-1'
                : 'tw-px-1.5 tw-pb-2 tw-pt-1',
            )}
          >
            {React.Children.toArray(children).map((c, i) => (
              <li key={i} className={clx(!collapsed && 'tw-w-full')}>
                {c}
              </li>
            ))}
          </ul>
        )}
      </>
    )
  }

  /* ---------- Link ---------- */
  if (href) {
    return withTooltip(
      <NavLink
        to={href}
        title={title}
        className={clx(wrapperClass, isActive && activeClass)}
      >
        {iconEl}
        {labelEl}
      </NavLink>,
      'tw-w-full',
    )
  }

  /* ---------- Button ---------- */
  if (onClick) {
    return withTooltip(
      <button
        type="button"
        onClick={onClick}
        name={name}
        disabled={disabled}
        title={title}
        className={clx(wrapperClass)}
      >
        {iconEl}
        {labelEl}
      </button>,
      'tw-w-full',
    )
  }

  /* ---------- Fallback ---------- */
  return withTooltip(
    <span className={clx(itemClass, collapsed && 'tw-justify-center')}>
      {iconEl}
      {labelEl}
    </span>,
    'tw-w-full',
  )
}
